import { useState, useMemo } from "react";
import { Search, Plus, Minus, Trash2, ShoppingCart, CheckCircle2, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { formatRupiah } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const catColors = {
  Makanan: "bg-orange-100 text-orange-700",
  Minuman: "bg-cyan-100 text-cyan-700",
  Snack: "bg-amber-100 text-amber-700",
  Rokok: "bg-red-100 text-red-700",
  Sembako: "bg-emerald-100 text-emerald-700",
  Kebutuhan: "bg-violet-100 text-violet-700",
  Umum: "bg-slate-100 text-slate-700",
};

export default function Kasir() {
  const { products, checkout } = useShop();
  const [query, setQuery] = useState("");
  const [kategori, setKategori] = useState("Semua");
  const [cart, setCart] = useState([]);
  const [bayar, setBayar] = useState("");
  const [struk, setStruk] = useState(null);

  const kategoriList = useMemo(() => {
    const set = new Set(products.map((p) => p.kategori));
    return ["Semua", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchQuery = p.nama.toLowerCase().includes(query.toLowerCase());
      const matchCat = kategori === "Semua" || p.kategori === kategori;
      return matchQuery && matchCat;
    });
  }, [products, query, kategori]);

  const addToCart = (product) => {
    if (product.stok <= 0) {
      toast.error(`Stok ${product.nama} habis`);
      return;
    }
    setCart((prev) => {
      const exist = prev.find((c) => c.id === product.id);
      if (exist) {
        if (exist.jumlah >= product.stok) {
          toast.error(`Stok ${product.nama} hanya ${product.stok}`);
          return prev;
        }
        return prev.map((c) =>
          c.id === product.id ? { ...c, jumlah: c.jumlah + 1 } : c
        );
      }
      return [...prev, { ...product, jumlah: 1 }];
    });
  };

  const changeQty = (id, delta) => {
    setCart((prev) =>
      prev
        .map((c) => {
          if (c.id !== id) return c;
          const newQty = c.jumlah + delta;
          if (newQty > c.stok) {
            toast.error(`Stok ${c.nama} hanya ${c.stok}`);
            return c;
          }
          return { ...c, jumlah: newQty };
        })
        .filter((c) => c.jumlah > 0)
    );
  };

  const removeItem = (id) => setCart((prev) => prev.filter((c) => c.id !== id));

  const total = cart.reduce((s, c) => s + c.harga_jual * c.jumlah, 0);
  const bayarNum = Number(bayar) || 0;
  const kembalian = bayarNum - total;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (bayar !== "" && bayarNum < total) {
      toast.error("Uang bayar kurang dari total");
      return;
    }
    try {
      const items = cart.map((c) => ({ product_id: c.id, jumlah: c.jumlah }));
      const trx = await checkout(items, bayar === "" ? null : bayarNum);
      setStruk(trx);
      setCart([]);
      setBayar("");
      toast.success("Transaksi berhasil, stok diperbarui");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transaksi gagal");
    }
  };

  return (
    <div className="flex flex-1 h-full overflow-hidden">
      {/* Product area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="px-8 pt-8 pb-4">
          <h2 className="font-heading font-bold text-2xl text-slate-900">Kasir</h2>
          <p className="text-sm text-slate-500 mt-1">Pilih barang untuk menambahkan ke keranjang</p>
          <div className="mt-5 relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="input-cari-produk"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari barang..."
              className="w-full bg-white border border-slate-200 rounded-xl pl-12 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900"
            />
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            {kategoriList.map((k) => (
              <button
                key={k}
                data-testid={`filter-kategori-${k}`}
                onClick={() => setKategori(k)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                  kategori === k
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-8 pb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((p) => {
              const low = p.stok <= p.stok_minimum;
              return (
                <button
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  onClick={() => addToCart(p)}
                  disabled={p.stok <= 0}
                  className="text-left bg-white rounded-xl border border-slate-200 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-md ${catColors[p.kategori] || catColors.Umum}`}>
                      {p.kategori}
                    </span>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded-md ${
                        p.stok <= 0
                          ? "bg-red-100 text-red-700"
                          : low
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      Stok {p.stok}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-slate-900 leading-snug line-clamp-2 min-h-[2.75rem]">
                    {p.nama}
                  </h3>
                  <p className="font-heading font-bold text-blue-600 text-lg mt-2">
                    {formatRupiah(p.harga_jual)}
                  </p>
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-slate-400 py-20">Tidak ada barang ditemukan</div>
          )}
        </div>
      </div>

      {/* Cart area */}
      <div className="w-96 bg-white border-l border-slate-200 flex flex-col h-full z-10">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">Keranjang</h3>
            <p className="text-xs text-slate-500">{cart.length} jenis barang</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="cart-items">
          {cart.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-slate-300">
              <ShoppingCart className="w-14 h-14 mb-3" strokeWidth={1.3} />
              <p className="text-sm text-slate-400">Keranjang masih kosong</p>
            </div>
          )}
          {cart.map((c) => (
            <div key={c.id} className="bg-slate-50 rounded-xl p-3 border border-slate-100" data-testid={`cart-item-${c.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                  <p className="font-semibold text-sm text-slate-900 leading-tight">{c.nama}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{formatRupiah(c.harga_jual)}</p>
                </div>
                <button
                  data-testid={`cart-remove-${c.id}`}
                  onClick={() => removeItem(c.id)}
                  aria-label="Hapus item"
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`cart-minus-${c.id}`}
                    onClick={() => changeQty(c.id, -1)}
                    aria-label="Kurangi"
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors active:scale-90"
                  >
                    <Minus className="w-4 h-4 text-slate-700" />
                  </button>
                  <span className="w-8 text-center font-bold text-slate-900" data-testid={`cart-qty-${c.id}`}>{c.jumlah}</span>
                  <button
                    data-testid={`cart-plus-${c.id}`}
                    onClick={() => changeQty(c.id, 1)}
                    aria-label="Tambah"
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors active:scale-90"
                  >
                    <Plus className="w-4 h-4 text-slate-700" />
                  </button>
                </div>
                <span className="font-heading font-bold text-slate-900">
                  {formatRupiah(c.harga_jual * c.jumlah)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 px-6 py-5 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-500 font-medium">Total</span>
            <span className="font-heading font-bold text-2xl text-slate-900" data-testid="cart-total">
              {formatRupiah(total)}
            </span>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Uang Dibayar (opsional)</label>
            <input
              data-testid="input-bayar"
              type="number"
              value={bayar}
              onChange={(e) => setBayar(e.target.value)}
              placeholder={formatRupiah(total)}
              className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900"
            />
          </div>
          {bayar !== "" && bayarNum >= total && (
            <div className="flex justify-between text-sm" data-testid="kembalian-preview">
              <span className="text-slate-500">Kembalian</span>
              <span className="font-bold text-emerald-600">{formatRupiah(kembalian)}</span>
            </div>
          )}
          <button
            data-testid="btn-checkout"
            onClick={handleCheckout}
            disabled={cart.length === 0}
            className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-xl px-6 py-4 transition-colors active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            Bayar Sekarang
          </button>
        </div>
      </div>

      {/* Struk dialog */}
      <Dialog open={!!struk} onOpenChange={() => setStruk(null)}>
        <DialogContent className="max-w-sm" data-testid="dialog-struk">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Transaksi Berhasil
            </DialogTitle>
          </DialogHeader>
          {struk && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {struk.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      {it.nama} <span className="text-slate-400">x{it.jumlah}</span>
                    </span>
                    <span className="font-medium text-slate-900">{formatRupiah(it.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed border-slate-200 pt-3 space-y-1.5">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>{formatRupiah(struk.total)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Bayar</span>
                  <span>{formatRupiah(struk.bayar)}</span>
                </div>
                <div className="flex justify-between text-sm text-emerald-600 font-semibold">
                  <span>Kembalian</span>
                  <span>{formatRupiah(struk.kembalian)}</span>
                </div>
                <div className="flex justify-between text-sm text-blue-600">
                  <span>Laba</span>
                  <span>{formatRupiah(struk.total_laba)}</span>
                </div>
              </div>
              <button
                data-testid="btn-tutup-struk"
                onClick={() => setStruk(null)}
                className="w-full bg-slate-900 text-white font-semibold rounded-xl py-3 mt-2 hover:bg-slate-800 transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" /> Tutup
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
